import { Layout } from "components/common"
import { useEffect, useRef, useMemo } from "react"
import React from "react"
import Play from "components/icons/play"
import ShortControls from "components/ShortControls"
import ShortNavigator from "components/ShortNavigator"
import useAutoPlayShort from "hooks/useAutoPlayShort"
import useShortScroll from "hooks/useShortScroll"
import useShortData from "hooks/useShortData"
import { ShortProvider, useShortContext } from "contexts/ShortContext"
import { SHORT_CONFIG, SHORT_KEYS } from "constants/short"
import dynamic from "next/dynamic"

const ShortPlayer = dynamic(() => import("components/ShortPlayer"), { ssr: false })

function ShortContent() {
  const {
    isPlaying,
    showControls,
    muted,
    volume,
    userInteracted,
    showAll,
    currentIndex,
    videoRefs,
    setCurrentIndex,
    setIsPlaying,
    togglePlay,
    handleUserInteraction,
    syncVideoState
  } = useShortContext()

  const { listShort, loading, error, loadMore, reload } = useShortData()

  const scrollRef = useRef(null)
  const videoContainersRef = useRef([])

  // Memoize displayed tags
  const displayed = useMemo(() => {
    const currentShort = listShort[currentIndex]
    if (!currentShort?.tag) return []
    return showAll ? currentShort.tag : currentShort.tag.slice(0, 3)
  }, [listShort, currentIndex, showAll])

  // Determine which videos should be loaded (preload strategy)
  const shouldLoadVideo = (index) => {
    return Math.abs(index - currentIndex) <= SHORT_CONFIG.PRELOAD_RANGE
  }

  const { scrollToNext, scrollToPrev } = useShortScroll({
    listShort,
    currentIndex,
    setCurrentIndex,
    setIsPlaying,
    scrollRef,
    videoContainersRef
  })

  useAutoPlayShort(videoRefs, currentIndex)

  // Load more shorts when near end
  useEffect(() => {
    if (!listShort.length || loading) return
    if (currentIndex >= listShort.length - 2) {
      loadMore()
    }
  }, [currentIndex, listShort.length, loading, loadMore])

  // Hide body overflow
  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [])

  // Sync video state when switching
  useEffect(() => {
    syncVideoState(currentIndex)
  }, [currentIndex, syncVideoState])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === SHORT_KEYS.ARROW_DOWN) {
        e.preventDefault()
        scrollToNext()
      } else if (e.key === SHORT_KEYS.ARROW_UP) {
        e.preventDefault()
        scrollToPrev()
      } else if (e.key === SHORT_KEYS.SPACE) {
        e.preventDefault()
        togglePlay()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [scrollToNext, scrollToPrev, togglePlay])

  // Show loading state
  if (loading && !listShort.length) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // Show error state
  if (error && !listShort.length) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-black text-white">
        <p className="text-lg mb-4">Không thể tải video</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#0966D4] rounded-full hover:bg-[#0857B8] transition-colors"
        >
          Thử lại
        </button>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden">
      <div
        ref={scrollRef}
        className="overflow-y-scroll snap-y snap-mandatory scroll-smooth bg-black h-screen mt-12 lg:mt-16"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none"
        }}
      >
        {listShort.map((item, i) => (
          <VideoItem
            key={`${item.id}-${i}`}
            item={item}
            index={i}
            currentIndex={currentIndex}
            isPlaying={isPlaying}
            shouldLoad={shouldLoadVideo(i)}
            muted={muted}
            volume={volume}
            userInteracted={userInteracted}
            showControls={showControls}
            displayed={displayed}
            listShort={listShort}
            videoContainersRef={videoContainersRef}
            videoRefs={videoRefs}
            thumbnail={item.thumbnail_vertical}
            onUserInteraction={handleUserInteraction}
            onTogglePlay={togglePlay}
            onReload={() => reload(item.id)}
          />
        ))}

        {/* Loading more indicator */}
        {loading && listShort.length > 0 && (
          <div className="h-screen flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      <ShortNavigator
        currentIndex={currentIndex}
        total={listShort.length}
        scrollToPrev={scrollToPrev}
        scrollToNext={scrollToNext}
      />
    </div>
  )
}

// Memoized VideoItem component
const VideoItem = React.memo(
  ({
    item,
    index,
    currentIndex,
    isPlaying,
    shouldLoad,
    muted,
    volume,
    userInteracted,
    showControls,
    displayed,
    listShort,
    videoContainersRef,
    videoRefs,
    thumbnail,
    onUserInteraction,
    onTogglePlay,
    onReload
  }) => {
    const isActive = index === currentIndex

    return (
      <div
        className="relative w-full h-screen flex items-center justify-center snap-start"
        ref={(el) => (videoContainersRef.current[index] = el)}
      >
        <div
          className="h-screen flex justify-center w-screen md:w-[545px] mx-auto bg-black relative"
          onMouseMove={onUserInteraction}
          onClick={onUserInteraction}
          onTouchStart={onUserInteraction}
        >
          {/* Player */}
          <ShortPlayer
            ref={(el) => (videoRefs.current[index] = el)}
            className="w-full h-full object-cover"
            image={thumbnail}
            src={shouldLoad ? item.url : null}
            muted={muted}
            userInteracted={userInteracted}
            loop
            volume={volume}
            isPlaying={isActive && isPlaying}
            onClick={onTogglePlay}
            onError={() => console.error("Video error on index", index)}
            onReload={onReload}
            showControls={showControls && isActive}
          />

          {/* Play button overlay */}
          {!isPlaying && isActive && (
            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onTogglePlay()
                }}
                className="text-white text-4xl rounded-full bg-[#000E2B]/50 w-[74px] h-[74px] p-2 flex items-center justify-center pointer-events-auto hover:bg-[#000E2B]/70 transition-colors"
                aria-label="Play video"
              >
                <Play />
              </button>
            </div>
          )}

          {/* Controls */}
          {(showControls || !isPlaying) && isActive && (
            <ShortControls item={item} displayed={displayed} currentIndex={currentIndex} listShort={listShort} />
          )}
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.index === nextProps.index &&
      prevProps.currentIndex === nextProps.currentIndex &&
      prevProps.isPlaying === nextProps.isPlaying &&
      prevProps.shouldLoad === nextProps.shouldLoad &&
      prevProps.muted === nextProps.muted &&
      prevProps.volume === nextProps.volume &&
      prevProps.userInteracted === nextProps.userInteracted &&
      prevProps.showControls === nextProps.showControls
    )
  }
)

VideoItem.displayName = "VideoItem"

// Main component with Provider
export default function Short() {
  return (
    <ShortProvider>
      <ShortContent />
    </ShortProvider>
  )
}

Short.Layout = Layout
